/* eslint-disable @next/next/no-img-element */
import BrandedButton from "../../../../shadcn/components/custom/BrandedButton";
import { Card } from "../../../../shadcn/components/ui/card";
import ChartLineUp from "../../../../icons/ChartLineUp";
import { formatNumber, normalizeUrl } from "services";
import VerifiedHammer from "../../../../icons/VerifiedHammer";

export type PropertyListType = "auction-properties" | "private-properties";

export interface PropertyDataInterface {
  propertyType: PropertyListType;
  listingId?: string;
  title: string;
  possession: string;
  priceTag: string;
  price?: number | string;
  area: string;
  auctionDate: string;
  hasImage: boolean;
  propertyImageUrl: string;
  showRequestImagesCta: boolean;
  isTagged: boolean;
  tag?: string;
  showTagText: boolean;
  tagText?: string;
  bankName?: string;
  bankLogo?: string;
  isVerified?: boolean;
  isLiked: boolean;
}

export interface PropertyCardGridViewPropsInterface {
  onCardClick: (property: PropertyDataInterface) => void;
  property: PropertyDataInterface;
  onRequestImagesClick: (property: PropertyDataInterface) => void;
}

export const PropertyCardGridView: React.FC<
  PropertyCardGridViewPropsInterface
> = ({
  onCardClick = (_property) => {},
  property,
  onRequestImagesClick = () => {},
}) => {
  return (
    <Card
      className="border border-gray-200 rounded-xl overflow-hidden capitalize bg-white hover:shadow-lg transition-shadow flex flex-col"
      onClick={() => onCardClick?.(property)}
    >
      <div className="relative h-[170px] bg-gray-100">
        {property?.hasImage ? (
          <div className="h-full">
            <img
              src={property?.propertyImageUrl}
              alt={property?.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = "none";
                e.currentTarget.nextElementSibling?.classList.remove("hidden");
              }}
            />
            {property?.showRequestImagesCta ? (
              <div
                className={`absolute inset-0  text-gray-500 text-sm flex flex-col gap-2 items-center justify-center`}
              >
                <BrandedButton
                  variant="outline"
                  onClick={() => {
                    onRequestImagesClick?.(property);
                  }}
                  className="mb-4 z-[11] bg-white"
                >
                  Request Images
                </BrandedButton>
              </div>
            ) : null}
          </div>
        ) : (
          <div
            className={`absolute inset-0 bg-gray-100 text-gray-500 text-sm flex flex-col gap-2 items-center justify-center`}
          >
            <p>Images not available</p>
            <BrandedButton
              variant="outline"
              onClick={() => {
                onRequestImagesClick?.(property);
              }}
              className="mb-4 z-[11] bg-white"
            >
              Request Images
            </BrandedButton>
          </div>
        )}

        <div className="absolute top-2 left-2 w-full flex justify-between gap-2">
          <div className="bg-white border border-orange-500 text-orange-600 px-3 py-1 rounded-full text-xs font-semibold">
            {property?.listingId || "N/A"}
          </div>
          {property?.isVerified && (
            <div>
              <VerifiedHammer />
            </div>
          )}
        </div>

        {property?.isTagged && (
          <div className="absolute bottom-0 left-0 z-10 bg-gradient-to-t from-black to-[#00000000] w-full pt-4 pb-3 lg:pt-8">
            {property?.tag && (
              <div
                className={
                  "ml-2 pl-1 pr-5 text-xs font-openSans font-semibold uppercase bg-[#F1EAFF] justify-self-start py-[2px]"
                }
                style={{
                  clipPath:
                    "polygon(0% 0%, 85% 0%, 100% 100%, 100% 100%, 0% 100%)",
                }}
              >
                {property?.tag || "Seen"}
              </div>
            )}
            {property?.showTagText && (
              <div className="flex items-start gap-2 px-2 pt-2 text-xs font-openSans font-bold text-white">
                <ChartLineUp />
                <div>{property?.tagText}</div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="p-4 flex-1 flex flex-col">
        <h3 className="font-semibold text-gray-900 mb-3 text-sm line-clamp-2">
          {property?.title}
        </h3>

        <div className="mb-3">
          <div className="text-xs text-gray-500 mb-1">{property?.priceTag}</div>
          <div className="text-orange-600 font-bold text-xl">
            ₹{formatNumber(property?.price)}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3 text-xs">
          {property?.propertyType === "auction-properties" && (
            <>
              <div>
                <div className="text-gray-500 mb-1">Auction Date</div>
                <div className="font-medium">{property?.auctionDate}</div>
              </div>

              <div>
                <div className="text-gray-500 mb-1">Possession</div>
                <div className="font-medium">{property?.possession}</div>
              </div>
            </>
          )}
          <div>
            <div className="text-gray-500 mb-1">Area</div>
            <div className="font-medium">{property?.area}</div>
          </div>
          {property?.propertyType === "auction-properties" && (
            <>
              {property?.bankLogo ? <img src={property?.bankLogo} /> : null}
              <div>
                <div className="text-gray-500 mb-1">Bank</div>
                <div className="font-medium">{property?.bankName}</div>
              </div>
            </>
          )}
        </div>
      </div>
      <div className="mt-auto p-3 border-t border-gray-200">
        <BrandedButton
          onClick={() =>
            window.open(
              normalizeUrl(
                `${process.env.NEXT_PUBLIC_WEB_URL}/${property?.propertyType}/${property?.listingId}`,
              ),
              "_blank",
            )
          }
          variant="primary"
          size="md"
          fullWidth
        >
          View Details
        </BrandedButton>
      </div>
    </Card>
  );
};
